import { HttpInterceptorFn, HttpErrorResponse, HttpStatusCode } from '@angular/common/http'
import { inject } from '@angular/core'
import { Router } from '@angular/router'

import { catchError, throwError } from 'rxjs'

import { AuthService } from '@yoshu-lib/core/auth/auth.service'
import { ToastService } from '@yoshu-lib/core/toast/toast.service'

import { TranslateService } from '@ngx-translate/core'

import { ApiError } from './error.model'
import { ENVIRONMENT } from '../tokens/environment.token'

/**
 * An HTTP interceptor function that handles errors in HTTP requests.
 *
 * This interceptor catches HTTP errors and performs specific actions based on the error status.
 * For example, if the error status is 401 (Unauthorized), it redirects the user to the login page.
 * Additionally, it logs the error to the console and rethrows it for further handling.
 *
 * @param req - The outgoing HTTP request.
 * @param next - The next interceptor or backend handler in the chain.
 * @returns An observable of the HTTP event stream, with error handling applied.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService)
  const toast = inject(ToastService)
  const translate = inject(TranslateService)
  const router = inject(Router)
  const env = inject(ENVIRONMENT)

  const isYoshuApiRequest = req.url.startsWith(env.apiUrl)

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Skip 409 Conflict errors - they are handled specifically by the upload dialog
      // for duplicate file detection
      if (error.status === HttpStatusCode.Conflict) {
        return throwError(() => error)
      }

      if (isYoshuApiRequest) {
        const yoshuError = error.error as ApiError

        if (typeof yoshuError.detail === 'string') {
          toast.error(yoshuError.detail)
        } else if (req.url.includes('/v1/auth/login')) {
          authService.logout().subscribe()
          toast.error(error.message)
        } else {
          const unknownErrorMessage = translate.instant('errors.unknown_error') as string
          toast.error(unknownErrorMessage)
        }
      }

      if (error.status === 401) {
        const isLoginRequest = req.url.includes('/auth/login')
        const isRefreshRequest = req.url.includes('/auth/refresh')

        if (!isLoginRequest && !isRefreshRequest) {
          router.navigate(['/'])
        }
      }

      console.error('HTTP error: ', error)

      return throwError(() => error)
    })
  )
}
